import { useState, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Search, Edit, FileText, Download, Users as UsersIcon, Link2, KeyRound, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { UserFormDialog } from "../components/users/UserFormDialog";
import { SetPasswordDialog } from "../components/users/SetPasswordDialog";

export default function UserManagement() {
    const { user: currentUser } = useAuth();
    const isAdmin = currentUser?.role === "admin";
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const [showPwDialog, setShowPwDialog] = useState(false);
    const [pwTarget, setPwTarget] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [editingUser, setEditingUser] = useState(null);
    const [form, setForm] = useState({
        name: "", email: "", password: "user123", organization: "",
        department: "", designation: "", employee_id: "", reporting_manager: ""
    });
    const navigate = useNavigate();

    const fetchUsers = async () => {
        try {
            const endpoint = isAdmin ? "/admin/users" : "/coach/users";
            const { data } = await api.get(endpoint);
            setUsers(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredUsers = users.filter(u => {
        const q = search.toLowerCase();
        return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) ||
            u.organization?.toLowerCase().includes(q) || u.department?.toLowerCase().includes(q);
    });

    const openCreate = () => {
        setEditingUser(null);
        setForm({ name: "", email: "", password: "user123", organization: "", department: "", designation: "", employee_id: "", reporting_manager: "" });
        setShowDialog(true);
    };

    const openEdit = (u) => {
        setEditingUser(u);
        setForm({
            name: u.name, email: u.email, password: "",
            organization: u.organization || "", department: u.department || "",
            designation: u.designation || "", employee_id: u.employee_id || "",
            reporting_manager: u.reporting_manager || ""
        });
        setShowDialog(true);
    };

    const handleSubmit = async () => {
        try {
            if (editingUser) {
                const update = {
                    name: form.name, organization: form.organization,
                    department: form.department, designation: form.designation,
                    employee_id: form.employee_id, reporting_manager: form.reporting_manager
                };
                await api.put(`/coach/users/${editingUser.id}`, update);
                toast.success("User updated");
            } else {
                const { data } = await api.post("/coach/users", form);
                const desc = data.email_sent
                    ? `Invite email sent to ${form.email}`
                    : data.assessment_link ? `Assessment link: ${data.assessment_link}` : undefined;
                toast.success("User created successfully", { description: desc, duration: 8000 });
                if (data.assessment_link) {
                    try { await navigator.clipboard.writeText(data.assessment_link); } catch (e) { console.error("Clipboard write failed:", e); }
                }
            }
            setShowDialog(false);
            fetchUsers();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const handleExportPDF = async (userId) => {
        try {
            const response = await api.get(`/reports/${userId}/pdf`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `report_${userId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch { toast.error("PDF export failed"); }
    };

    const copyAssessmentLink = async (userId) => {
        try {
            const { data } = await api.get(`/coach/users/${userId}/link`);
            await navigator.clipboard.writeText(data.link);
            toast.success("Assessment link copied to clipboard!", { description: data.link });
        } catch { toast.error("Failed to get assessment link"); }
    };

    const sendInviteEmail = async (userId, userName) => {
        try {
            const { data } = await api.post(`/coach/users/${userId}/send-invite`);
            if (data.email_sent) {
                toast.success(`Invite email sent to ${userName}`);
            } else {
                toast.info("Email delivery attempted", { description: "The email may not have been delivered. You can also copy the link directly." });
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail || "Failed to send invite email"));
        }
    };

    const openSetPassword = (u) => {
        setPwTarget(u);
        setNewPassword("");
        setShowPwDialog(true);
    };

    const handleSetPassword = async () => {
        if (!pwTarget) return;
        if (newPassword.length < 6) {
            toast.error("Password must be at least 6 characters");
            return;
        }
        try {
            await api.put(`/admin/users/${pwTarget.id}/password`, { password: newPassword });
            toast.success(`Password updated for ${pwTarget.email}`);
            setShowPwDialog(false);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    const basePath = isAdmin ? "/admin" : "/coach";

    if (loading) return (
        <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
        </div>
    );

    return (
        <div data-testid="user-management" className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: "Outfit" }}>
                        {isAdmin ? "All Assessment Users" : "My Users"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">{users.length} users total</p>
                </div>
                {!isAdmin && (
                    <Button data-testid="create-user-btn" onClick={openCreate} style={{ backgroundColor: "#4A7AB5" }} className="gap-2">
                        <Plus className="h-4 w-4" /> Add User
                    </Button>
                )}
            </div>

            <Card className="border-border bg-card">
                <CardHeader className="pb-3">
                    <div className="relative w-full sm:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input data-testid="user-search" placeholder="Search users..." value={search}
                            onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-background border-border" />
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-border">
                                    <TableHead className="text-muted-foreground">Name</TableHead>
                                    {isAdmin && <TableHead className="text-muted-foreground">Coach</TableHead>}
                                    <TableHead className="text-muted-foreground">Organization</TableHead>
                                    <TableHead className="text-muted-foreground">Department</TableHead>
                                    <TableHead className="text-muted-foreground">Status</TableHead>
                                    <TableHead className="text-muted-foreground">Score</TableHead>
                                    <TableHead className="text-muted-foreground">Archetype</TableHead>
                                    <TableHead className="text-muted-foreground">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUsers.map((u) => {
                                    const a = u.assessment;
                                    const status = a?.status || "not_started";
                                    const score = a?.scores?.total_weighted_score;
                                    const archetype = a?.scores?.archetype?.name;
                                    return (
                                        <TableRow key={u.id} className="border-border hover:bg-secondary/50">
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 rounded-lg bg-[#F5A623]/10">
                                                        <UsersIcon className="h-4 w-4 text-[#F5A623]" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-foreground text-sm">{u.name}</p>
                                                        <p className="text-xs text-muted-foreground">{u.email}</p>
                                                    </div>
                                                </div>
                                            </TableCell>
                                            {isAdmin && <TableCell className="text-sm text-muted-foreground">{u.coach_name || "-"}</TableCell>}
                                            <TableCell className="text-sm text-muted-foreground">{u.organization || "-"}</TableCell>
                                            <TableCell className="text-sm text-muted-foreground">{u.department || "-"}</TableCell>
                                            <TableCell>
                                                <Badge variant={status === "completed" ? "default" : status === "in_progress" ? "secondary" : "outline"}
                                                    className={status === "completed" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : ""}>
                                                    {status.replace("_", " ")}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-sm font-medium text-foreground">
                                                {score != null ? score.toFixed(1) : "-"}
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground max-w-[130px] truncate">{archetype || "-"}</TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    {!isAdmin && (
                                                        <Button data-testid={`edit-user-${u.id}`} variant="ghost" size="sm" onClick={() => openEdit(u)}>
                                                            <Edit className="h-4 w-4 text-muted-foreground" />
                                                        </Button>
                                                    )}
                                                    {status !== "completed" && (
                                                        <>
                                                        <Button data-testid={`copy-link-${u.id}`} variant="ghost" size="sm"
                                                            onClick={() => copyAssessmentLink(u.id)} title="Copy assessment link"
                                                            className="text-[#F5A623] hover:text-[#F5A623]/80">
                                                            <Link2 className="h-4 w-4" />
                                                        </Button>
                                                        <Button data-testid={`email-invite-${u.id}`} variant="ghost" size="sm"
                                                            onClick={() => sendInviteEmail(u.id, u.name || u.email)} title="Email assessment invite"
                                                            className="text-[#4A7AB5] hover:text-[#4A7AB5]/80">
                                                            <Mail className="h-4 w-4" />
                                                        </Button>
                                                        </>
                                                    )}
                                                    {isAdmin && (
                                                        <Button data-testid={`set-pw-${u.id}`} variant="ghost" size="sm"
                                                            onClick={() => openSetPassword(u)} title="Set password"
                                                            className="text-muted-foreground hover:text-foreground">
                                                            <KeyRound className="h-3.5 w-3.5" />
                                                        </Button>
                                                    )}
                                                    {status === "completed" && (
                                                        <>
                                                            <Button data-testid={`view-report-${u.id}`} variant="ghost" size="sm"
                                                                onClick={() => navigate(`${basePath}/reports/${u.id}`)} className="text-primary hover:text-primary/80">
                                                                <FileText className="h-4 w-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="sm" onClick={() => handleExportPDF(u.id)}
                                                                className="text-muted-foreground hover:text-foreground">
                                                                <Download className="h-4 w-4" />
                                                            </Button>
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                                {filteredUsers.length === 0 && (
                                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No users found</TableCell></TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            <UserFormDialog open={showDialog} onOpenChange={setShowDialog} editingUser={editingUser} form={form} setForm={setForm} onSubmit={handleSubmit} />
            <SetPasswordDialog open={showPwDialog} onOpenChange={setShowPwDialog} target={pwTarget} password={newPassword} setPassword={setNewPassword} onSubmit={handleSetPassword} />
        </div>
    );
}
